import { Icon } from "@uzverse/ui";
import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { LookSettings } from "../loco/LookSettings.js";
import { useWorldT } from "../i18n.js";
import { layoutStore, useLayout } from "../layout.js";
import type { QualitySetting } from "../perf/quality.js";
import { playUi, setSoundEnabled, soundEnabled } from "../sfx.js";
import { hudUi, useHudUi } from "./hudUi.js";
import { navStore, useNavState } from "./nav.js";
import type { NavStore } from "./nav.js";
import { Segmented, SettingRow, Switch } from "./SettingsParts.js";
import { TimeControl } from "./TimeControl.js";
import { VehicleSoundSettings } from "../vehicles/audio/VehicleSoundSettings.js";

const QUALITIES: readonly QualitySetting[] = ["auto", "low", "high"];
const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="hud-section" aria-labelledby={id} data-testid={`settings-${id}`}>
      <h3 id={id}>{title}</h3>
      <div className="hud-section-body">{children}</div>
    </section>
  );
}

/**
 * Every setting of the world in one place, opened by the gear (or Esc): Display, Controls, Sound
 * and World. A popover above the gear on wide screens, a bottom sheet on phones.
 */
export function SettingsSheet({ nav = navStore }: { nav?: NavStore }) {
  const t = useWorldT();
  const { settingsOpen, prefs } = useHudUi();
  const s = useNavState(nav);
  const ls = useLayout();
  const [sound, setSound] = useState(soundEnabled);
  const root = useRef<HTMLDivElement>(null);
  const docked = ls.layout === "docked";

  useEffect(() => {
    if (!settingsOpen) return;
    const before = document.activeElement as HTMLElement | null;
    root.current?.querySelector<HTMLElement>(".hud-sheet-close")?.focus({ preventScroll: true });
    return () => {
      if (before && document.contains(before)) before.focus({ preventScroll: true });
    };
  }, [settingsOpen]);

  if (!settingsOpen) return null;
  const close = () => hudUi.openSettings(false);
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      close();
      return;
    }
    if (e.key !== "Tab") return;
    const items = [...(root.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])];
    if (items.length === 0) return;
    const first = items[0]!;
    const last = items[items.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };
  return (
    <>
      <div className="hud-scrim" onPointerDown={close} aria-hidden="true" />
      <div
        ref={root}
        className="hud-sheet"
        data-testid="world-settings"
        role="dialog"
        aria-label={t("game.settingsTitle")}
        onKeyDown={onKey}
      >
        <header className="hud-sheet-head">
          <h2>{t("game.settingsTitle")}</h2>
          <button
            type="button"
            className="hud-sheet-close"
            data-testid="world-settings-close"
            aria-label={t("game.closeSettings")}
            title={t("game.closeSettings")}
            onClick={close}
          >
            <Icon name="x" size={16} />
          </button>
        </header>
        <div className="hud-sheet-body">
          <Section id="display" title={t("game.secDisplay")}>
            <SettingRow label={t("nav.quality")} stacked>
              <Segmented
                label={t("nav.quality")}
                testId="world-quality"
                options={QUALITIES.map((q) => ({
                  id: q,
                  label: t(`nav.quality${q[0]!.toUpperCase()}${q.slice(1)}`),
                  testId: `quality-${q}`,
                  pressed: s.quality === q,
                }))}
                onPick={(q) => nav.setQuality(q as QualitySetting)}
              />
            </SettingRow>
            <SettingRow label={t("game.layout")} hint={t("game.layoutHint")} stacked>
              <Segmented
                label={t("game.layout")}
                options={[
                  {
                    id: "immersive",
                    label: t("game.layoutImmersive"),
                    testId: "layout-immersive",
                    pressed: !docked,
                  },
                  {
                    id: "docked",
                    label: t("game.layoutDocked"),
                    testId: "layout-docked",
                    pressed: docked,
                  },
                ]}
                onPick={(id) => {
                  if (ls.fs !== "off") return;
                  layoutStore.dispatch({
                    type: "setLayout",
                    layout: id === "docked" ? "docked" : "immersive",
                  });
                }}
              />
            </SettingRow>
            <Switch
              label={t("game.showMinimap")}
              checked={prefs.minimap}
              onChange={(on) => hudUi.setPrefs({ minimap: on })}
              testId="world-minimap-toggle"
            />
            <Switch
              label={t("game.showCompass")}
              checked={prefs.compass}
              onChange={(on) => hudUi.setPrefs({ compass: on })}
              testId="world-compass-toggle"
            />
            <Switch
              label={t("game.showLabels")}
              checked={prefs.modeLabels}
              onChange={(on) => hudUi.setPrefs({ modeLabels: on })}
              testId="world-labels-toggle"
            />
          </Section>
          <Section id="controls" title={t("game.secControls")}>
            <LookSettings />
          </Section>
          <Section id="audio" title={t("game.secAudio")}>
            <Switch
              label={t("game.sound")}
              hint={t("game.soundHint")}
              checked={sound}
              testId="world-sound-toggle"
              onChange={(on) => {
                setSoundEnabled(on);
                setSound(on);
                playUi("up");
              }}
            />
            <VehicleSoundSettings />
          </Section>
          <Section id="world" title={t("game.secWorld")}>
            <TimeControl />
          </Section>
        </div>
      </div>
    </>
  );
}
